import React, { useState, useEffect, useMemo } from 'react';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  ArrowPathIcon,
  XMarkIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import TransactionTable from './TransactionTable';
import DateFormatSelector from './DateFormatSelector';
import { calculateQualityStats, normalizePartialDate, splitForeignAmountTransactions } from '../../utils/transactionValidator';
import { getFunctions, httpsCallable } from 'firebase/functions';
// featureFlags import removed - using HTTP architecture only

const PreviewModal = ({
  isOpen,
  transactions: initialTransactions,
  fileName,
  transactionId,
  statementYear,
  onAccept,
  onClose
}) => {
  const [transactions, setTransactions] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReporting, setIsReporting] = useState(false);
  const [reportSuccess, setReportSuccess] = useState(false);
  const [showIssuesOnly, setShowIssuesOnly] = useState(false);
  const [amountsNegated, setAmountsNegated] = useState(false);

  // Initialize transactions and normalize dates using statement year
  useEffect(() => {
    if (initialTransactions && Array.isArray(initialTransactions)) {
      // Split foreign amount transactions into separate bank charge entries
      const splitTransactions = splitForeignAmountTransactions(initialTransactions);

      // Normalize partial dates (like "03 Aug") to full dates (like "03/08/2018")
      const normalizedTransactions = splitTransactions.map(trans => ({
        ...trans,
        date: normalizePartialDate(trans.date, statementYear)
      }));

      setTransactions(normalizedTransactions);
    }
  }, [initialTransactions, statementYear]);

  // Handle modal visibility with animation
  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      const timer = setTimeout(() => setIsVisible(false), 150);
      return () => clearTimeout(timer);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isProcessing && onClose) {
        console.log('🔑 Escape key pressed - closing preview modal');
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen, isProcessing, onClose]);


  // Calculate quality stats
  const qualityStats = useMemo(() => {
    return calculateQualityStats(transactions);
  }, [transactions]);

  // Auto-reset filter when no issues remain (e.g., user deleted all error rows)
  useEffect(() => {
    if (!qualityStats.hasIssues) {
      setShowIssuesOnly(false);
    }
  }, [qualityStats.hasIssues]);

  // Handle transaction changes from table
  const handleTransactionsChange = (updatedTransactions) => {
    setTransactions(updatedTransactions);
  };

  const handleNegateAmounts = () => {
    const updatedTransactions = transactions.map(t => {
      const parsed = parseFloat(t.amount);
      if (isNaN(parsed)) return t;
      return { ...t, amount: String(-parsed) };
    });
    setTransactions(updatedTransactions);
    setAmountsNegated(prev => !prev);
  };

  // Report issues to admin
  const handleReportIssues = async () => {
    if (isReporting) return;

    setIsReporting(true);
    setReportSuccess(false);

    try {
      const functions = getFunctions();
      const reportDataIssues = httpsCallable(functions, 'reportDataIssues');

      await reportDataIssues({
        transactionId,
        fileName,
        issues: {
          unknownDates: qualityStats.unknownDates,
          unknownDescriptions: qualityStats.unknownDescriptions,
          unknownAmounts: qualityStats.unknownAmounts,
          invalidDates: qualityStats.invalidDates,
          invalidDescriptions: qualityStats.invalidDescriptions,
          invalidAmounts: qualityStats.invalidAmounts,
          incompleteDates: qualityStats.incompleteDates
        },
        transactionCount: transactions.length
      });

      setReportSuccess(true);
      setTimeout(() => setReportSuccess(false), 3000);

      console.log('✅ Issue report sent successfully');
    } catch (error) {
      console.error('Failed to report issues:', error);
      alert('Failed to send issue report. Please try again or contact support.');
    } finally {
      setIsReporting(false);
    }
  };

  // Accept preview - behavior depends on architecture
  const handleAcceptPreview = async () => {
    if (isProcessing) return;

    // Confirm if there are issues
    if (qualityStats.hasIssues) {
      const totalIssues = qualityStats.unknownDates + qualityStats.unknownDescriptions + qualityStats.unknownAmounts +
                          qualityStats.invalidDates + qualityStats.invalidDescriptions + qualityStats.invalidAmounts +
                          qualityStats.incompleteDates;
      const confirmed = window.confirm(
        `There are ${totalIssues} data quality issues. ` +
        'Do you want to proceed anyway? You can edit the transactions or report the issues first.'
      );

      if (!confirmed) return;
    }

    setIsProcessing(true);

    try {
      // HTTP ARCHITECTURE: Call acceptPreview backend function
      console.log('📝 Calling acceptPreview backend function');

      const functions = getFunctions();
      const acceptPreview = httpsCallable(functions, 'acceptPreview');

      await acceptPreview({
        transactionId,
        editedTransactions: transactions
      });

      console.log('✅ Preview accepted, transaction marked as completed');

      // Close modal (downloads happen from transaction history)
      if (onAccept) {
        onAccept(); // Just close modal, no parameters
      }

    } catch (error) {
      console.error('Failed to accept preview:', error);
      setIsProcessing(false);

      // Show error alert to user
      alert(`Failed to process preview: ${error.message || 'Unknown error'}. Please try again.`);
      // Keep modal open so user can retry
    }
  };


  if (!isVisible) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        isOpen
          ? 'opacity-100 backdrop-blur-sm bg-black/50'
          : 'opacity-0 pointer-events-none'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preview-modal-title"
    >
      <div
        className={`relative w-full max-w-6xl h-[90vh] bg-white rounded-xl shadow-2xl transform transition-all duration-300 flex flex-col ${
          isOpen ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 pb-4 border-b border-gray-200">
          <div>
            <h2
              id="preview-modal-title"
              className="text-xl font-semibold text-gray-900"
            >
              Preview & Edit Transactions
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {fileName} • {transactions.length} transaction{transactions.length !== 1 ? 's' : ''}
            </p>
          </div>
          {/* Close button */}
          {onClose && !isProcessing && (
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close preview"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          )}
        </div>

        {/* Date Format Selector & Error Filter */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
          <div className="flex items-center justify-between">
            {/* Left side: Filter toggle buttons */}
            {qualityStats.hasIssues && (
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setShowIssuesOnly(false)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    !showIssuesOnly
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <span>Show All</span>
                </button>
                <button
                  onClick={() => setShowIssuesOnly(true)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    showIssuesOnly
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <FunnelIcon className="h-4 w-4" />
                  <span>Show Errors Only</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                    showIssuesOnly
                      ? 'bg-blue-700 text-white'
                      : 'bg-amber-100 text-amber-800'
                  }`}>
                    {qualityStats.unknownDates + qualityStats.invalidDates + qualityStats.incompleteDates +
                     qualityStats.unknownDescriptions + qualityStats.invalidDescriptions +
                     qualityStats.unknownAmounts + qualityStats.invalidAmounts}
                  </span>
                </button>
              </div>
            )}

            <button
              onClick={handleNegateAmounts}
              className={`flex items-center space-x-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                amountsNegated
                  ? 'bg-amber-100 text-amber-800 hover:bg-amber-200'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              title={amountsNegated ? 'Restore original amounts' : 'Negate all amounts (+/-)'}
            >
              <span>+/−</span>
            </button>

            {/* Right side: Date format selector */}
            <DateFormatSelector />
          </div>
        </div>

        {/* Table Content */}
        <div className="flex-1 overflow-hidden p-6">
          <TransactionTable
            transactions={transactions}
            onTransactionsChange={handleTransactionsChange}
            statementYear={statementYear}
            readonly={isProcessing}
            showIssuesOnly={showIssuesOnly}
          />
        </div>

        {/* Footer Actions */}
        <div className="flex items-center justify-between p-6 pt-4 border-t border-gray-200 bg-gray-50">
          {/* Left side: Report Issues button */}
          <div className="flex items-center space-x-3">
            {qualityStats.hasIssues && (
              <button
                onClick={handleReportIssues}
                disabled={isReporting}
                className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-gray-600 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
              >
                {isReporting ? (
                  <>
                    <ArrowPathIcon className="h-4 w-4 animate-spin" />
                    <span>Sending Report...</span>
                  </>
                ) : (
                  <>
                    <EnvelopeIcon className="h-4 w-4" />
                    <span>Report Issues</span>
                  </>
                )}
              </button>
            )}

            {/* Success feedback */}
            {reportSuccess && (
              <div className="flex items-center space-x-2 text-sm text-emerald-600">
                <CheckCircleIcon className="h-5 w-5" />
                <span className="font-medium">Report sent successfully</span>
              </div>
            )}
          </div>

          {/* Right side: Accept Preview button */}
          <button
            onClick={handleAcceptPreview}
            disabled={isProcessing}
            className="flex items-center space-x-2 px-6 py-2 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
          >
            {isProcessing ? (
              <>
                <ArrowPathIcon className="h-4 w-4 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <>
                <CheckCircleIcon className="h-4 w-4" />
                <span>Accept Preview</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PreviewModal;
